import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { BookmarkIcon, MessageCircle, Settings, Save } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

export default function Profile() {
  const [favorites, setFavorites] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const { user, checkAuth } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFavorites();
    loadConsultations();
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (name !== user.name) payload.name = name;
      if (email !== user.email) payload.email = email;
      if (newPassword) payload.new_password = newPassword;

      await api.put('/auth/me', payload);
      toast.success('Profile updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      await checkAuth();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const { data } = await api.get('/favorites');
      setFavorites(data);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const loadConsultations = async () => {
    try {
      const { data } = await api.get('/consultations');
      setConsultations(data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load consultations:', error);
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-12 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Welcome, {user?.name}
            </h1>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
              {user?.email}
            </p>
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 hover:-translate-y-1"
            style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: showSettings ? '#5B7C99' : '#F3EFE7', color: showSettings ? '#FDFBF7' : '#2C2A29', fontWeight: 600 }}
            data-testid="settings-toggle"
          >
            <Settings className="w-4 h-4" />
            <span>{showSettings ? 'Close Settings' : 'Account Settings'}</span>
          </button>
        </div>

        {showSettings && (
          <div className="p-8 rounded-3xl mb-12" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Account Settings
            </h2>
            <form onSubmit={handleSaveSettings} className="space-y-5 max-w-2xl">
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }} data-testid="settings-name" />
              </div>
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }} data-testid="settings-email" />
              </div>
              <div className="pt-4 border-t" style={{ borderColor: '#E6E2D8' }}>
                <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>New Password (leave blank to keep current)</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }} data-testid="settings-new-password" />
              </div>
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>Current Password (required to save any changes)</label>
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required placeholder="Confirm your identity" className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }} data-testid="settings-current-password" />
              </div>
              <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }} data-testid="save-settings">
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <BookmarkIcon className="w-6 h-6" style={{ color: '#E57A44' }} />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                Saved Articles
              </h2>
            </div>
            {favorites.length > 0 ? (
              <div className="space-y-4">
                {favorites.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="block p-4 rounded-2xl transition-all duration-300 hover:-translate-y-1"
                    style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}
                  >
                    <h3 className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                      {article.title}
                    </h3>
                    <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                      {article.category}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No saved articles yet</p>
            )}
          </div>

          {user?.role === 'admin' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6" style={{ color: '#E57A44' }} />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                Recent Consultations
              </h2>
            </div>
            {consultations.length > 0 ? (
              <div className="space-y-4">
                {consultations.map((consultation) => (
                  <Link
                    key={consultation.id}
                    to={`/consultation/${consultation.id}`}
                    className="block p-4 rounded-2xl transition-all duration-300 hover:-translate-y-1"
                    style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}
                  >
                    <h3 className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                      {consultation.title}
                    </h3>
                    <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                      Status: {consultation.status}
                    </p>
                  </Link>
                ))}
                <Link
                  to="/consultations"
                  className="block text-center py-3 rounded-xl transition-all duration-300"
                  style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }}
                >
                  View All Consultations
                </Link>
              </div>
            ) : (
              <div>
                <p className="mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                  No consultations yet
                </p>
                <Link
                  to="/consultations"
                  className="inline-block px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                >
                  Request a Consultation
                </Link>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
